import { useEffect, useState } from "react";

import { readableDetail } from "../lib/api";

const API = "/api";
const SEATS = [["owner", "Owner"], ["broker", "Broker"], ["lender", "Lender"]];

// v4 Part 2 — the homepage's modal. It carries REQUEST AN INVITATION (there is no public sign-up:
// an account exists only by accepting an invitation) and nothing else that touches a session.
// Sign in stays on its own surface by founder ruling §1 (the owner desk and the console are two
// failure models that "do not share a screen, handler or session shape"), restyled to look like
// this box — so this modal never holds a password.
export function RequestInviteModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [seat, setSeat] = useState("owner");
  const [human, setHuman] = useState("");
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API}/identity/invitation-request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, seat, human_check: human }),
      });
      const body = await r.json().catch(() => ({}));
      // The ONE call in the app that does not go through apiFetch (it is pre-session, and the modal
      // must not depend on the authed client). It still goes through the same normaliser: a FastAPI
      // 422 `detail` is an ARRAY, and `new Error(array)` would put "[object Object]" in front of a
      // stranger on the homepage.
      if (!r.ok) {
        throw new Error(readableDetail(body.detail, r.status)
                        || "that didn't go through — try again in a moment");
      }
      setSent(body.email || email);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const ready = email.includes("@") && human.trim() !== "" && !busy;

  return (
    <div className="pub-scrim" data-testid="request-invite-modal"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pub-modal" role="dialog" aria-modal="true" aria-label="Request an invitation">
        <div className="pub-modal-top">
          <h2 className="pub-modal-h">{sent ? "Request received" : "Request an invitation"}</h2>
          <button className="pub-modal-x" data-testid="request-invite-close" onClick={onClose}
                  aria-label="Close">✕</button>
        </div>

        {sent ? (
          <>
            <div className="pub-done" data-testid="request-invite-done">
              Thanks, we have {sent}. Juno is in private release and invitations go out by hand. If
              one comes, it will be from notify.junocre.com.
            </div>
            <p className="pub-fine">
              Already invited? <a href="/desk" data-testid="request-invite-signin">Sign in</a>
            </p>
          </>
        ) : (
          <form className="pub-modal" style={{ border: 0, padding: 0, boxShadow: "none", gap: 16 }}
                onSubmit={submit}>
            <p>Juno is in private release. Invitations go out by hand.</p>
            <label className="pub-field">
              <span className="pub-label">Email</span>
              <input className="pub-input" type="email" required autoFocus value={email}
                     data-testid="request-invite-email" autoComplete="email"
                     onChange={(e) => setEmail(e.target.value)} />
            </label>
            <div className="pub-field">
              <span className="pub-label">I am a —</span>
              <div className="pub-seats">
                {SEATS.map(([v, label]) => (
                  <button type="button" key={v} data-testid={`request-invite-seat-${v}`}
                          className={"pub-seat" + (seat === v ? " pub-seat-on" : "")}
                          onClick={() => setSeat(v)}>{label}</button>
                ))}
              </div>
            </div>
            <label className="pub-field">
              <span className="pub-label">Human check · what is 4 + 3?</span>
              <input className="pub-input" required value={human} inputMode="numeric"
                     data-testid="request-invite-human"
                     onChange={(e) => setHuman(e.target.value)} />
            </label>
            {err && <p className="pub-err" data-testid="request-invite-error">{err}</p>}
            <button className="pub-btn" type="submit" disabled={!ready}
                    data-testid="request-invite-submit">
              {busy ? "Sending…" : "Request an invitation →"}
            </button>
            <p className="pub-fine">
              We use your email only to answer this request. Never for marketing, never shared.
              {" "}<a href="/privacy">Privacy</a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
